import type { ComponentType, FC } from 'react'
import { useState } from 'react'

import { ExpandableDescription } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon'

import { usePolicyId } from '@/components/ecom-policy/hooks/use-policy-id'
import { useSelectProduct } from '@/components/ecom-policy/hooks/use-select-product'
import { usePolicyContext } from '@/components/ecom-policy/policy/Policy.context'
import type { Product } from '@/components/ecom-policy/types'
import { Translation } from '@/components/i18n/Translation'
import { useCart } from '@/contexts/cart/cart-context'

import { ActionCardButton } from '../common/product-card/ActionCardButton'
import { ProductCardWithInfo } from '../common/product-card/ProductCardWithInfo'
import { ProductInfo, type ProductInfoProps } from '../common/product-card/ProductInfo'

import { parseInStock } from '../../helpers'

export const MultipleProductGroup: FC<{
  searchQuery: string
  selectedGroupProductId?: number
  description: string | null
  groupName: string
  groupedProducts: Product[]
  isAccessory: boolean
  allocation: Record<number, { covered: number; userPrice: number }>
  coverLeft: number
  isSelectionDisabled: boolean
  isProductPriceVisible: boolean
  onModalClose: () => void
}> = ({
  searchQuery,
  selectedGroupProductId,
  description,
  groupedProducts,
  isAccessory,
  groupName,
  allocation,
  coverLeft,
  isSelectionDisabled,
  isProductPriceVisible,
  onModalClose,
}) => {
  const [isNextPage, setIsNextPage] = useState(false)
  const [moreInfoProduct, setMoreInfoProduct] = useState<ProductInfoProps | null>(null)
  // Max one product might be selected from a group
  const [groupSelectedProductId, setGroupSelectedProductId] = useState<number | null>(
    selectedGroupProductId || null
  )
  const policyId = usePolicyId()
  const policyCtx = usePolicyContext()
  const { incrementQty, decrementQty, qtyOf, getItemInCart } = useCart()
  const { handleAccessorySelect, handleGroupedProductSelect } = useSelectProduct()

  const [grossSalaryTaxBracket] = policyCtx.grossSalaryTaxBracket
  const [selectedAccessoriesIds] = policyCtx.selectedAccessoriesIds
  const [productQuantityById, setProductQuantityById] = policyCtx.productQuantityById

  const onProductSelect = (product: Product) => {
    if (isSelectionDisabled) return
    if (product.isAccessory) {
      handleAccessorySelect({ product })
    } else {
      handleGroupedProductSelect({
        product,
        groupSelectedProductId,
        setGroupSelectedProductId,
        onModalClose,
      })
    }
  }

  const onQuantityChange = (id: number, newQuantity: number, isIncrement: boolean) => {
    if (isSelectionDisabled) return
    setProductQuantityById({
      ...productQuantityById,
      [id]: newQuantity,
    })
    if (isIncrement) {
      incrementQty(policyId, id)
    } else {
      decrementQty(policyId, id)
    }
  }

  if (isNextPage && moreInfoProduct != null) {
    const isSelectedOnAnotherPolicy = Boolean(
      groupedProducts.some(product => {
        const item = getItemInCart(product.id)
        return (
          item &&
          !item.isGreenpoolProduct &&
          Number(item.policyId) !== policyId &&
          !item.product?.isMandatory
        )
      })
    )
    const isSelected = Boolean(
      moreInfoProduct.product.isAccessory
        ? selectedAccessoriesIds?.includes(moreInfoProduct.product.id)
        : groupSelectedProductId === moreInfoProduct.product.id
    )
    return (
      <div className="pb-4">
        <div className="text-left">
          <Button
            size="small"
            variant="tertiary"
            className="m-0 px-0"
            Icon={<ArrowLeftIcon />}
            onClick={() => setIsNextPage(false)}
          >
            <Translation id="Go Back" />
          </Button>
        </div>

        <ProductInfo
          {...moreInfoProduct}
          selectBtn={
            <ActionCardButton
              className="w-full sm:w-fit"
              size="regular"
              isSingle={moreInfoProduct.isSingle}
              isSelected={isSelected}
              actionDisabled={isSelectedOnAnotherPolicy || isSelectionDisabled}
              onCardSelect={() => onProductSelect(moreInfoProduct.product)}
            />
          }
        />
      </div>
    )
  }

  return (
    <div>
      <h3>{groupName}</h3>

      {description && (
        <ExpandableDescription
          description={description}
          showFullText={false}
          TranslationComponent={Translation as ComponentType}
        />
      )}

      <div className="mt-4">
        <div className="mobile:grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] mobile:gap-3 grid grid-cols-[repeat(auto-fill,_minmax(10rem,_1fr))] justify-center gap-2 sm:gap-4 md:grid-cols-[repeat(auto-fill,_minmax(15rem,_1fr))]">
          {groupedProducts.map(product => {
            const { id } = product
            const isSelected = Boolean(
              isAccessory ? selectedAccessoriesIds?.includes(id) : groupSelectedProductId === id
            )
            const userPrice =
              isSelected && allocation[id]
                ? allocation[id]?.userPrice
                : Math.max(0, product.price - coverLeft)
            const quantity = qtyOf(policyId, id)

            // Products marked mandatory should not be visible in product selection.
            if (product.isMandatory) return null

            return (
              <ProductCardWithInfo
                key={id}
                isSingle
                isSelected={isSelected}
                productTitle={{
                  productName: product.alias || product.name,
                  productQuerySearch: searchQuery,
                }}
                productImg={{
                  src: product.imgUrl || '',
                  assetType: product.assetType || 'OTHER',
                }}
                productPrice={{
                  employeePrice: isSelectionDisabled
                    ? Math.max(0, product.price)
                    : Math.max(0, userPrice),
                  productPrice: Math.max(0, product.price),
                  isFromPriceTextVisible: false,
                  grossSalaryTaxBracket: grossSalaryTaxBracket,
                  isProductPriceVisible,
                }}
                productInStock={parseInStock(product.inStock)}
                productQuantity={{
                  isQuantitySelectionAvailable: Boolean(isSelected && product.quantityLimit > 1),
                  quantity: quantity,
                  isQuantityPlusDisabled: quantity + 1 > product.quantityLimit,
                  onPlusClick: e => {
                    e.stopPropagation()
                    onQuantityChange(id, quantity + 1, true)
                  },
                  onMinusClick: e => {
                    e.stopPropagation()
                    if (quantity === 1) {
                      onProductSelect(product)
                      return
                    }
                    onQuantityChange(id, quantity - 1, false)
                  },
                }}
                product={product}
                isSelectionDisabled={isSelectionDisabled}
                onCardSelect={() => onProductSelect(product)}
                showNextPage={() => setIsNextPage(true)}
                setMoreInfoProduct={setMoreInfoProduct}
              />
            )
          })}
        </div>

        <footer className="mt-3 text-center">
          <Button variant="tertiary" onClick={onModalClose} className="m-0 min-w-[8rem]">
            <Translation id="Lukk" />
          </Button>
        </footer>
      </div>
    </div>
  )
}
